/** Nested Confirmed / Proposed / Rejected under each Rules pipeline stage. */
export type RulesPipelineNestedStage = 'step1' | 'step2a' | 'step2b' | 'step3' | 'step4' | 'step5';
export type RulesPipelineNestedSection = 'confirmed' | 'proposed' | 'rejected';

export type RulesParentAccordionOpen = {
  proposals: boolean;
  pipelineStepRules: boolean;
  merchantSuppliers: boolean;
  supplierProposals: boolean;
  appRules: boolean;
  supplierRules: boolean;
  vatRules: boolean;
  specialCaseRules: boolean;
};

export type RulesNestedAccordionOpen = Record<
  RulesPipelineNestedStage,
  Record<RulesPipelineNestedSection, boolean>
>;

export type RulesFilterAccordionCounts = {
  /** Step 3 confirmed+proposed rows (excludes rejected drafts). */
  classificationConfirmedProposedRows: number;
  /** Step 3 confirmed excluding pipeline cashflow heading rules. */
  classificationConfirmedExcludingPipeline: number;
  classificationProposed: number;
  classificationRejected: number;
  /** Keep Step 3 parent + Proposed open for deep-linked P-C / P-A filters. */
  keepClassificationOpen: boolean;
  /** Step 1 pipeline cashflow confirmed rows. */
  pipelineStep1Confirmed: number;
  merchantSupplierConfirmed: number;
  supplierConfirmed: number;
  supplierProposed: number;
  supplierRejected: number;
  /** Step 2b confirmed+proposed rows (excludes rejected). */
  supplierConfirmedProposedRows: number;
  vatConfirmed: number;
  vatProposed: number;
  vatRejected: number;
  specialCaseConfirmed: number;
};

/**
 * When Rules tab filters/search are active, which parent + nested accordions should open
 * so matching Confirmed / Proposed / Rejected rows are visible (not only the stage parent).
 */
export function computeRulesFilterAccordionOpenState(
  counts: RulesFilterAccordionCounts
): { parents: RulesParentAccordionOpen; nested: RulesNestedAccordionOpen } {
  const classificationHasHit =
    counts.classificationConfirmedProposedRows > 0 ||
    counts.classificationRejected > 0 ||
    counts.keepClassificationOpen;
  const supplierHasHit =
    counts.supplierConfirmedProposedRows > 0 || counts.supplierRejected > 0;
  const vatHasHit =
    counts.vatConfirmed + counts.vatProposed + counts.vatRejected > 0;

  const parents: RulesParentAccordionOpen = {
    proposals: classificationHasHit,
    pipelineStepRules: counts.pipelineStep1Confirmed > 0,
    merchantSuppliers: counts.merchantSupplierConfirmed > 0,
    supplierProposals: supplierHasHit,
    appRules: false,
    supplierRules: false,
    vatRules: vatHasHit,
    specialCaseRules: counts.specialCaseConfirmed > 0,
  };

  const nested: RulesNestedAccordionOpen = {
    step1: {
      confirmed: counts.pipelineStep1Confirmed > 0,
      proposed: false,
      rejected: false,
    },
    step2a: {
      confirmed: counts.merchantSupplierConfirmed > 0,
      proposed: false,
      rejected: false,
    },
    step2b: {
      confirmed: counts.supplierConfirmed > 0,
      proposed: counts.supplierProposed > 0,
      rejected: counts.supplierRejected > 0,
    },
    step3: {
      confirmed: counts.classificationConfirmedExcludingPipeline > 0,
      proposed: counts.classificationProposed > 0 || counts.keepClassificationOpen,
      rejected: counts.classificationRejected > 0,
    },
    step4: {
      confirmed: counts.vatConfirmed > 0,
      proposed: counts.vatProposed > 0,
      rejected: counts.vatRejected > 0,
    },
    step5: {
      confirmed: counts.specialCaseConfirmed > 0,
      proposed: false,
      rejected: false,
    },
  };

  return { parents, nested };
}
